/* eslint-disable */
import React, { useEffect, useState, useRef } from 'react';
import {getAllUserBlocks,updateBlockPositions} from '../../../api/blocks' // Assuming this path is correct
import { toast } from 'react-toastify';

// Helper function to create the structure needed for select options
// and to preserve the original block data.
const mapBlocksToOptions = (blocks) => {
  return blocks.map(block => ({
    value: String(block.id), 
    text: block.name, 
    originalBlock: { ...block } 
  }));
};

const OrganizeBlocks = ({
  onSave,
  onCancel,
  title = "Organize Blocks",
  role
}) => {
  const [hiddenBlockOptions, setHiddenBlockOptions] = useState([]);
  const [visibleBlockOptions, setVisibleBlockOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectHiddenRef = useRef(null);
  const selectVisibleRef = useRef(null);

  const [hiddenFilter, setHiddenFilter] = useState('');
  const [visibleFilter, setVisibleFilter] = useState('');
  const [lastVisibleSelectedIndex, setLastVisibleSelectedIndex] = useState(-1);

  useEffect(() => {
    const fetchAndSetBlocks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedBlocks = await getAllUserBlocks(); 

        const initialVisibleUnsorted = fetchedBlocks.filter(block => block.isActive);
        const initialHiddenUnsorted = fetchedBlocks.filter(block => !block.isActive);

        const sortedInitialVisible = [...initialVisibleUnsorted].sort((a, b) => {
            const posA = a.pos !== undefined && a.pos !== null ? a.pos : Infinity;
            const posB = b.pos !== undefined && b.pos !== null ? b.pos : Infinity;
            if (posA === Infinity && posB === Infinity) {
                return a.name.localeCompare(b.name);
            }
            return posA - posB;
        });

        const sortedInitialHidden = [...initialHiddenUnsorted].sort((a,b) => {
            const posA = a.pos !== undefined && a.pos !== null ? a.pos : Infinity;
            const posB = b.pos !== undefined && b.pos !== null ? b.pos : Infinity;
            if (posA === Infinity && posB === Infinity) {
                return a.name.localeCompare(b.name);
            }
            return posA - posB;
        });

        setVisibleBlockOptions(mapBlocksToOptions(sortedInitialVisible));
        setHiddenBlockOptions(mapBlocksToOptions(sortedInitialHidden)); 
      } catch (err) {
        console.error("OrganizeBlocks - Failed to fetch blocks:", err);
        setError(err.message || "An error occurred while fetching blocks.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetBlocks();
  }, []); 

  const filteredHiddenBlockOptions = hiddenBlockOptions.filter(option =>
    option.text.toLowerCase().includes(hiddenFilter.toLowerCase())
  );

  const filteredVisibleBlockOptions = visibleBlockOptions.filter(option =>
    option.text.toLowerCase().includes(visibleFilter.toLowerCase())
  );

  const handleMoveToVisible = () => {
    if (!selectHiddenRef.current) return;
    const selectedValues = Array.from(selectHiddenRef.current.selectedOptions).map(opt => opt.value);
    if (selectedValues.length === 0) return;
    const blocksToMove = hiddenBlockOptions.filter(opt => selectedValues.includes(opt.value));
    setVisibleBlockOptions(prevVisible => {
      const newVisible = [...prevVisible];
      let insertAtIndex = prevVisible.length; 
      if (selectVisibleRef.current && lastVisibleSelectedIndex !== -1 && lastVisibleSelectedIndex < prevVisible.length) {
        insertAtIndex = lastVisibleSelectedIndex + 1;
      }
      blocksToMove.forEach((blockOpt, index) => {
        newVisible.splice(insertAtIndex + index, 0, blockOpt);
      });
      return newVisible;
    });
    setHiddenBlockOptions(prevHidden => prevHidden.filter(opt => !selectedValues.includes(opt.value)));
    setLastVisibleSelectedIndex(-1); 
  };

  const handleMoveToHidden = () => {
    if(role !=='Zone owner') {
         toast.info(
                <div style={{ margin: '5px' }}>
                 Only the Zone owner can hide blocks
                </div>,
                {
                  position: toast.POSITION.BOTTOM_CENTER,
                  autoClose: 5000,
                },
              );
      return false;
    }
    if (!selectVisibleRef.current) return;
    const selectedValues = Array.from(selectVisibleRef.current.selectedOptions).map(opt => opt.value);
    if (selectedValues.length === 0) return;
    const blocksToMove = visibleBlockOptions.filter(opt => selectedValues.includes(opt.value));
    setHiddenBlockOptions(prevHidden => {
        const newHidden = [...prevHidden, ...blocksToMove];
        return newHidden.sort((a,b) => {
            const posA = a.originalBlock.pos !== undefined && a.originalBlock.pos !== null ? a.originalBlock.pos : Infinity;
            const posB = b.originalBlock.pos !== undefined && b.originalBlock.pos !== null ? b.originalBlock.pos : Infinity;
            if (posA === Infinity && posB === Infinity) { return a.text.localeCompare(b.text); }
            return posA - posB;
        });
    });
    setVisibleBlockOptions(prevVisible => prevVisible.filter(opt => !selectedValues.includes(opt.value)));
    setLastVisibleSelectedIndex(-1);
  };

  const handleMoveUp = () => {
    if (!selectVisibleRef.current) return;
    const selectedValues = Array.from(selectVisibleRef.current.selectedOptions).map(opt => opt.value);
    if (selectedValues.length === 0) return;
    setVisibleBlockOptions(prevVisible => {
      let updatedVisible = [...prevVisible];
      for (let i = 0; i < updatedVisible.length; i++) {
        if (selectedValues.includes(updatedVisible[i].value)) {
          if (i > 0 && !selectedValues.includes(updatedVisible[i-1].value)) {
            [updatedVisible[i], updatedVisible[i - 1]] = [updatedVisible[i - 1], updatedVisible[i]];
          } 
        }
      }
      return updatedVisible; 
    });
  };

  const handleMoveDown = () => {
    if (!selectVisibleRef.current) return;
    const selectedValues = Array.from(selectVisibleRef.current.selectedOptions).map(opt => opt.value);
    if (selectedValues.length === 0) return;
    setVisibleBlockOptions(prevVisible => {
      let updatedVisible = [...prevVisible];
      for (let i = updatedVisible.length - 1; i >= 0; i--) {
        if (selectedValues.includes(updatedVisible[i].value)) {
          if (i < updatedVisible.length - 1 && !selectedValues.includes(updatedVisible[i+1].value)) {
            [updatedVisible[i], updatedVisible[i + 1]] = [updatedVisible[i + 1], updatedVisible[i]];
          }
        }
      }
      return updatedVisible;
    });
  };
  
  const handleVisibleSelectionChange = () => {
    if (selectVisibleRef.current) {
        const firstSelectedIndex = Array.from(selectVisibleRef.current.options).findIndex(option => option.selected);
        setLastVisibleSelectedIndex(firstSelectedIndex !== -1 ? firstSelectedIndex : selectVisibleRef.current.options.length -1);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      const finalBlocks = [];
      let currentOrder = 0; 
      visibleBlockOptions.forEach(opt => {
        finalBlocks.push({ ...opt.originalBlock, isActive: true, pos: currentOrder++ });
      });
      hiddenBlockOptions.forEach(opt => {
        finalBlocks.push({ ...opt.originalBlock, isActive: false, pos: opt.originalBlock.pos });
      });
      try {
        await updateBlockPositions(finalBlocks); 
        onSave(finalBlocks); 
      } catch (saveError) {
        console.error("OrganizeBlocks - Failed to save block positions:", saveError);
      }
    }
  };

  // Styles
  const overallContainerStyle = { // Renamed from containerStyle to avoid confusion with a potential inner one
    border: '1px solid #AC207D',      // Border for the whole modal/component
    borderRadius: '8px',          // Rounded corners for the whole modal
    backgroundColor: '#f9f9f9',   // Background for the content area below header
    fontFamily: 'Arial, sans-serif',
    overflow: 'hidden', // Ensures border-radius clips children like the header
  };

  const headerBarStyle = { // <<<< NEW STYLE FOR THE HEADER BAR >>>>
    height: '50px',
    backgroundColor: '#AC207D', // Your specified background color
    color: 'white',             // White text
    display: 'flex',
    alignItems: 'center',       // Vertically center text
    justifyContent: 'flex-start',   // Horizontally center text
    fontSize: '20px',           // Adjust font size as needed
    fontWeight: 'bold', 
    paddingLeft:'20px',
            // Make title bold
    // Removed padding from here to make it flush, padding is on overallContainerStyle if needed for content
  };
  
  const contentWrapperStyle = { // <<<< NEW STYLE for content area below header >>>>
    padding: '20px', // The padding previously in containerStyle
  };

  // const titleStyle = { textAlign: 'center', marginBottom: '20px', fontSize: '24px', color: '#333' }; // No longer used for main title
  const picklistContainerStyle = { display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', marginBottom: '20px' };
  const listColumnStyle = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '40%' };
  const selectStyle = { width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '4px', padding: '5px', overflowY: 'auto', backgroundColor: 'white' };
  const filterInputStyle = { width: 'calc(100% - 12px)', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' };
  const buttonGroupStyle = { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', margin: '0 20px', paddingTop: '50px' };
  const buttonStyle = { padding: '8px 12px', margin: '5px 0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#e7e7e7', minWidth: '40px', textAlign: 'center' };
  const actionsContainerStyle = { display: 'flex', justifyContent: 'flex-end', marginTop: '20px',marginRight:'80px' }; // Keep your margin if needed
  const actionButtonStyle = { ...buttonStyle, marginLeft: '10px', backgroundColor: '#AC207D', color: 'white', borderColor: '#AC207D'};
  const cancelButtonStyle = { ...buttonStyle, marginLeft: '10px', backgroundColor: '#6c757d', color: 'white', borderColor: '#6c757d'};
  const loadingErrorStyle = { textAlign: 'center', margin: '20px', fontSize: '18px', color: 'red' };

  if (isLoading) {
    return <div style={overallContainerStyle}><div style={headerBarStyle}>{title}</div><p style={{textAlign: 'center', fontSize: '18px', padding: '20px'}}>Loading blocks...</p></div>;
  }
  if (error) {
    return <div style={overallContainerStyle}><div style={headerBarStyle}>{title}</div><p style={loadingErrorStyle}>Error: {error}</p></div>;
  }

  return (
    <div style={overallContainerStyle}> {/* Outermost div with border and overall structure */}
      <div style={headerBarStyle}> {/* <<<< HEADER BAR >>>> */}
        {title} 
      </div>
      <div style={contentWrapperStyle}> {/* <<<< WRAPPER FOR CONTENT BELOW HEADER >>>> */}
        <div style={picklistContainerStyle}>
          {/* Hidden Blocks List (Left) */}
          <div style={listColumnStyle}>
            <h3>Hidden Blocks</h3>
            <input
              type="text"
              placeholder="Filter hidden Blocks..."
              value={hiddenFilter}
              onChange={(e) => setHiddenFilter(e.target.value)}
              style={filterInputStyle}
            />
            <select multiple ref={selectHiddenRef} style={selectStyle}>
              {filteredHiddenBlockOptions.map(option => (
                <option key={option.value} value={option.value} style={{ padding: '5px' }}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons (Center) */}
          <div style={buttonGroupStyle}>
            <button onClick={handleMoveToVisible} style={buttonStyle} title="Move to Visible">{'>>'}</button>
            <button onClick={handleMoveToHidden} style={buttonStyle} title="Move to Hidden">{'<<'}</button>
          </div>

          {/* Visible Blocks List (Right) */}
          <div style={listColumnStyle}>
            <h3>Visible Blocks</h3>
            <input
              type="text"
              placeholder="Filter visible Blocks..." 
              value={visibleFilter}
              onChange={(e) => setVisibleFilter(e.target.value)}
              style={filterInputStyle}
            />
            <select multiple ref={selectVisibleRef} style={selectStyle} onChange={handleVisibleSelectionChange}>
              {filteredVisibleBlockOptions.map(option => (
                <option key={option.value} value={option.value} style={{ padding: '5px' }}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
          
          {/* Up/Down Buttons for Visible List */}
          <div style={buttonGroupStyle}>
              <button onClick={handleMoveUp} style={buttonStyle} title="Move Up">{'^'}</button>
              <button onClick={handleMoveDown} style={buttonStyle} title="Move Down">{'v'}</button>
          </div>
        </div>

        {/* Save and Cancel Buttons */}
        <div style={actionsContainerStyle}>
          <button onClick={onCancel} style={cancelButtonStyle}>Cancel</button>
          <button onClick={handleSave} style={actionButtonStyle}>Save</button>
        </div>
      </div> {/* End of contentWrapperStyle */}
    </div>
  );
};

export default OrganizeBlocks;
